/**
 * FinalizedBreakdownSummary — read-only hierarchy for transaction/item totals.
 * All values come from the finalized TransactionBreakdown; this component owns
 * presentation only and preserves the established data-testid contract.
 */
import * as React from 'react';
import { cn } from '@/lib/utils';
import type { AllocationState, TransactionBreakdown } from '@/lib/transactionBreakdown';

interface FinalizedBreakdownSummaryProps {
  breakdown: TransactionBreakdown;
  fmt: (value: number) => string;
  unavailableText?: string;
  className?: string;
}

const LABEL: Record<AllocationState, string> = {
  balanced: 'Balanced',
  unallocated: 'Unallocated',
  overallocated: 'Overallocated',
  incomplete: 'Incomplete',
};

export function FinalizedBreakdownSummary({
  breakdown,
  fmt,
  unavailableText = 'Unavailable',
  className,
}: FinalizedBreakdownSummaryProps): React.ReactElement {
  const b = breakdown;
  const show = (value: number | null) => (value === null ? unavailableText : fmt(value));
  const delta = b.allocationDelta === null
    ? unavailableText
    : `${b.allocationDelta > 0 ? '+' : ''}${fmt(b.allocationDelta)}`;
  const allocationTone = {
    balanced: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
    unallocated: 'border-amber-500/25 bg-amber-500/10 text-amber-600 dark:text-amber-400',
    overallocated: 'border-destructive/25 bg-destructive/10 text-destructive',
    incomplete: 'border-border bg-muted/40 text-muted-foreground',
  }[b.allocationState];

  return (
    <section
      data-testid="finalized-breakdown-summary"
      className={cn(
        'rounded-xl border border-border/70 bg-muted/20 p-4 shadow-sm',
        className,
      )}
    >
      <div className="flex items-end justify-between gap-3 border-b border-border/70 pb-4">
        <div className="min-w-0">
          <p className="text-xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
            Net total
          </p>
          <p className="mt-1 text-[11px] text-muted-foreground">
            Transaction amount after discounts
          </p>
        </div>
        <p
          data-testid="finalized-breakdown-transaction-net"
          className="shrink-0 text-xl font-bold tabular-nums text-foreground"
          aria-label={`Transaction net ${show(b.transactionNet)}`}
        >
          {show(b.transactionNet)}
        </p>
      </div>

      <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-xs">
        <div>
          <dt className="text-muted-foreground">Transaction gross</dt>
          <dd data-testid="finalized-breakdown-transaction-gross" className="mt-0.5 font-medium tabular-nums text-foreground">
            {show(b.transactionGross)}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Transaction discount</dt>
          <dd data-testid="finalized-breakdown-transaction-discount" className="mt-0.5 font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
            −{show(b.transactionDiscount)}
          </dd>
        </div>
      </dl>

      <div className="mt-4 border-t border-border/70 pt-4">
        <div className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold text-foreground">Item allocation</p>
            <p className="mt-0.5 text-[11px] text-muted-foreground">How line items map to the net total</p>
          </div>
          <p data-testid="finalized-breakdown-item-net" className="shrink-0 text-base font-semibold tabular-nums text-foreground">
            {show(b.itemNet)}
          </p>
        </div>

        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
          <div>
            <dt className="text-muted-foreground">Items gross</dt>
            <dd data-testid="finalized-breakdown-item-gross" className="mt-0.5 font-medium tabular-nums text-foreground">
              {show(b.itemGross)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Items discount</dt>
            <dd data-testid="finalized-breakdown-item-discount" className="mt-0.5 font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
              −{show(b.itemDiscount)}
            </dd>
          </div>
        </dl>
      </div>

      <div className="mt-4 border-t border-border/70 pt-4">
        <div
          data-testid="finalized-breakdown-allocation"
          className={cn('flex items-center justify-between gap-3 rounded-lg border px-3 py-2 text-xs', allocationTone)}
        >
          <span className="font-medium">Allocation</span>
          <span className="inline-flex items-center gap-1.5 tabular-nums">
            <span className="font-semibold">{LABEL[b.allocationState]}</span>
            <span className="text-[10px] opacity-80">({delta})</span>
          </span>
        </div>
      </div>
    </section>
  );
}

export default FinalizedBreakdownSummary;
